'use client';

import { faAngleDown, faMessageLines } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { Textarea } from '@pickleballinc/ui/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@pickleballinc/ui/components/ui/tooltip';
import clsx from 'clsx';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import CheckboxForm from '@/components/atoms/CheckboxForm';
import { ActionButtons } from '@/components/molecules';
import { ModalWrapper } from '@/components/organisms';
import { SendTextData, sendTextSchema } from '@/entities';

export interface GroupPlayers {
	uuid: string;
	id: string;
	name: string;
	isDisabled: boolean;
}

interface SendTextFormProps {
	title: string;
	players: GroupPlayers[];
	onSubmit: (data: SendTextData) => void;
	onClose: () => void;
}

export const SendTextForm = ({ title, players, onSubmit, onClose }: SendTextFormProps) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const form = useForm<SendTextData>({
		resolver: zodResolver(sendTextSchema),
		defaultValues: {
			message: '',
			players: players.reduce((acc: any, player: GroupPlayers) => {
				acc[player.id] = false;
				return acc;
			}, {})
		}
	});

	const { formState, register, handleSubmit } = form;

	const handleSubmitForm = async (data: SendTextData) => {
		setIsSubmitting(true);
		try {
			await onSubmit(data);

			form.reset();
			onClose();
		} catch (error) {
			console.error('Error submitting form:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const watchedPlayers = form.watch('players');
	const isPlayerSelected = Object.values(watchedPlayers).some((selected) => selected) || players.length == 0;

	return (
		<ModalWrapper title={title} onClose={onClose} className="max-w-[524px] gap-0 p-6">
			<FormProvider {...form}>
				<form onSubmit={handleSubmit(handleSubmitForm)}>
					<hr className="my-4" />

					{players.length > 0 && (
						<div className="scrollbar-thin mb-5 flex max-h-[170px] flex-col gap-3 overflow-auto">
							{players.map((player: GroupPlayers) => (
								<div className="flex" key={player.uuid}>
									{player.isDisabled ? (
										<TooltipProvider delayDuration={0} skipDelayDuration={0}>
											<Tooltip>
												<TooltipTrigger asChild>
													<div className="!text-gray-400">
														<CheckboxForm
															name={`players.${player.id}`}
															label={player.name}
															isDisabled={player.isDisabled}
														/>
													</div>
												</TooltipTrigger>
												<TooltipPortal>
													<TooltipContent>
														<p>This player has opted out of SMS messages.</p>
													</TooltipContent>
												</TooltipPortal>
											</Tooltip>
										</TooltipProvider>
									) : (
										<div className="flex flex-row items-center gap-2">
											<CheckboxForm name={`players.${player.id}`} label={player.name} />
											<FontAwesomeIcon className="text-darkorange-400" icon={faMessageLines} size="sm" />
										</div>
									)}
								</div>
							))}
						</div>
					)}

					{/* 
						hasError={!!formState.errors.message}
						hint={formState.errors.message?.message?.toString()}
						*/}
					<Textarea
						{...register('message')}
						className={clsx('pt-4 focus-visible:ring-0', {
							'border-error-600 focus-visible:ring-1 focus-visible:ring-error-600': !!formState.errors.message
						})}
						placeholder="Your message..."
						rows={6}
					/>
					{!!formState.errors.message && <p className="text-sm text-error-600">{formState.errors.message?.message?.toString()}</p>}

					<details className="group my-6">
						<summary className="flex size-full cursor-pointer items-center justify-between rounded-t-lg px-2.5 py-3 text-base font-medium leading-5 text-[#EF6820] group-open:border group-open:border-solid group-open:border-gray-300">
							<div>Read text message restrictions</div>
							<FontAwesomeIcon icon={faAngleDown} className="ml-2 size-5 transition-transform duration-300 group-open:rotate-180" />
						</summary>
						<div className="overflow-hidden rounded-b-lg border-x border-b border-solid border-gray-300 px-3.5   py-2.5 text-sm text-gray-700 opacity-100 transition-all duration-300 ease-in-out">
							<div className="flex flex-col gap-4 text-sm leading-5 text-gray-900">
								<p>
									<strong>Absolutely no</strong> content pertaining to CBD, Alcohol, Marijuana, or any substance whatsoever.
								</p>
								<p>
									In addition, there is to be <strong>absolutely no</strong> advertising used with this text service.
								</p>
								<p>
									<strong>ALL</strong> messages are to be related to the league ONLY.
								</p>
								<p>
									<strong>ANY</strong> violation will be an immediate termination of your league.
								</p>
							</div>
						</div>
					</details>

					<ActionButtons
						className="w-full"
						submitButtonClassName="w-full text-white"
						closeButtonClassName="w-full"
						onClose={onClose}
						submitButtonText="Send"
						closeButtonText="Cancel"
						submitButtonDisabled={isSubmitting || !isPlayerSelected}
						submitTooltipMessage="Please select at least one player and enter a message."
					/>
				</form>
			</FormProvider>
		</ModalWrapper>
	);
};
